import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AutoAwesome as CoachIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Comment as CommentIcon,
  Edit,
  Mic,
  MicOff,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Fade,
  IconButton,
  LinearProgress,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useJotCoachTracking } from '@services/statsig';
import { useModal } from '@store/hooks';

import { MODAL_TYPES } from '../../../components/modals/constants';
import { EditNoteLoadingModal } from '../../notes/components/EditNoteLoadingModal';
import { VoiceInstructionInput } from '../../notes/components/VoiceInstructionInput';
import { useEditNoteWithContext } from '../../notes/hooks/useEditNoteWithContext';
import type {
  JotCoachDocumentationAnalysis,
  JotCoachProviderQuestion,
  JotCoachSynthesizedFeedback,
} from '../store/hooks';

interface JotCoachOverviewProps {
  analysis: JotCoachDocumentationAnalysis;
  synthesizedFeedback?: JotCoachSynthesizedFeedback | null;
  sessionId?: string;
  noteId?: string;
  isLoading?: boolean;
  organizationId?: string | null;
  sectionCount?: number;
  providerQuestions?: JotCoachProviderQuestion[];
}

export const JotCoachOverview: React.FC<JotCoachOverviewProps> = ({
  analysis,
  synthesizedFeedback,
  sessionId,
  noteId,
  isLoading = false,
  organizationId,
  sectionCount = 0,
  providerQuestions = [],
}) => {
  const { open } = useModal();
  const {
    trackOverviewRendered,
    trackOverviewViewed,
    trackFeedbackUseful,
    trackFeedbackNotUseful,
    trackFeedbackComment,
  } = useJotCoachTracking();

  // Dictation state
  const [instructionText, setInstructionText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const {
    editNote,
    isLoading: isEditingNote,
    taskId,
    error: editError,
    status,
    ready,
    clearError,
  } = useEditNoteWithContext();

  const hasTooManySections = sectionCount > 16;

  // Question rotation state
  const [questionIndex, setQuestionIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  // Rotate through questions every 12 seconds
  useEffect(() => {
    if (providerQuestions.length <= 2) return; // No need to rotate if 2 or fewer

    const interval = setInterval(() => {
      setFadeIn(false);
      setTimeout(() => {
        setQuestionIndex(prev => (prev + 2) % providerQuestions.length);
        setFadeIn(true);
      }, 600); // Wait for fade out
    }, 12000);

    return () => clearInterval(interval);
  }, [providerQuestions.length]);

  // Get current 2 questions to display
  const getCurrentQuestions = () => {
    if (providerQuestions.length <= 2) {
      return providerQuestions;
    }
    // Handle wrapping around the array
    const questions = [];
    for (let i = 0; i < 2; i++) {
      const index = (questionIndex + i) % providerQuestions.length;
      questions.push(providerQuestions[index]);
    }
    return questions;
  };

  // Refs for tracking deduplication
  const hasBeenRenderedRef = useRef(false);
  const hasBeenViewedRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  // Track render when card enters viewport
  useEffect(() => {
    if (!sessionId || !noteId || hasBeenRenderedRef.current || !cardRef.current) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasBeenRenderedRef.current) {
            trackOverviewRendered({
              jotcoach_session_id: sessionId,
              note_id: noteId,
              quality_score: analysis.overall_quality_score,
            });
            hasBeenRenderedRef.current = true;
          }
        });
      },
      { threshold: 0.5 },
    );

    observer.observe(cardRef.current);

    return () => observer.disconnect();
  }, [sessionId, noteId, analysis.overall_quality_score, trackOverviewRendered]);

  // Track hover/view
  const handleMouseEnter = useCallback(() => {
    if (!sessionId || !noteId || hasBeenViewedRef.current) return;

    trackOverviewViewed({
      jotcoach_session_id: sessionId,
      note_id: noteId,
      quality_score: analysis.overall_quality_score,
    });
    hasBeenViewedRef.current = true;
  }, [sessionId, noteId, analysis.overall_quality_score, trackOverviewViewed]);

  // Track feedback clicks
  const handleFeedbackUseful = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (sessionId && noteId) {
        trackFeedbackUseful({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          item_type: 'overview',
          item_id: 'overview',
        });
      }
      open(MODAL_TYPES.JOTCOACH_FEEDBACK, {
        itemType: 'session_overall',
        itemId: 'session_overall',
        sessionId,
        noteId,
        initialUseful: true,
        itemContent: {
          title: 'JotCoach Analysis',
          description: analysis.tone_for_provider,
        },
      });
    },
    [sessionId, noteId, analysis.tone_for_provider, trackFeedbackUseful, open],
  );

  const handleFeedbackNotUseful = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (sessionId && noteId) {
        trackFeedbackNotUseful({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          item_type: 'overview',
          item_id: 'overview',
        });
      }
      open(MODAL_TYPES.JOTCOACH_FEEDBACK, {
        itemType: 'session_overall',
        itemId: 'session_overall',
        sessionId,
        noteId,
        initialUseful: false,
        itemContent: {
          title: 'JotCoach Analysis',
          description: analysis.tone_for_provider,
        },
      });
    },
    [sessionId, noteId, analysis.tone_for_provider, trackFeedbackNotUseful, open],
  );

  const handleFeedbackComment = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (sessionId && noteId) {
        trackFeedbackComment({
          jotcoach_session_id: sessionId,
          note_id: noteId,
          item_type: 'overview',
          item_id: 'overview',
        });
      }
      open(MODAL_TYPES.JOTCOACH_FEEDBACK, {
        itemType: 'session_overall',
        itemId: 'session_overall',
        sessionId,
        noteId,
        initialUseful: null,
        itemContent: {
          title: 'JotCoach Analysis',
          description: analysis.tone_for_provider,
        },
      });
    },
    [sessionId, noteId, analysis.tone_for_provider, trackFeedbackComment, open],
  );

  const handleDictationSubmit = async () => {
    if (!instructionText.trim() || !noteId) {
      return;
    }

    await editNote({
      note_id: noteId,
      organization_id: organizationId,
      text: instructionText.trim(),
    });

    setInstructionText('');
  };

  const handleVoiceTranscript = (transcript: string) => {
    setInstructionText(prev => (prev ? `${prev} ${transcript}` : transcript));
  };

  const handleRecordingToggle = () => {
    setIsRecording(!isRecording);
  };

  const handleCloseModal = () => {
    clearError();
  };

  if (isLoading) {
    return (
      <Card sx={{ mb: 2, borderRadius: 1 }}>
        <CardContent sx={{ p: 2 }}>
          <LinearProgress />
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', fontSize: '0.875rem' }}>
            Loading JotCoach analysis...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      ref={cardRef}
      onMouseEnter={handleMouseEnter}
      sx={{
        mb: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'primary.main',
        boxShadow: 2,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
        }}
      >
        <CoachIcon sx={{ color: 'primary.main', fontSize: 24 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '1rem' }}>
          JotCoach Analysis
        </Typography>
      </Box>

      <CardContent sx={{ p: 2, pt: 0 }}>
        {/* Two-column layout: Feedback on left, Dictation on right */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2,
            mb: 3,
          }}
        >
          {/* Left: Detailed Feedback */}
          <Box sx={{ minWidth: 0 }}>
            {synthesizedFeedback?.detailed_feedback ? (
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'primary.light',
                  borderLeftWidth: 4,
                  height: '100%',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                  }}
                >
                  {synthesizedFeedback.detailed_feedback}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  {analysis.quality_summary}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right: Post-Session Dictation */}
          {noteId && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'background.paper',
                borderRadius: 1,
                border: '2px solid',
                borderColor: 'primary.main',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.5,
                height: '100%',
                minWidth: 0,
                width: '100%',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Mic sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
                  Post-Session Dictation
                </Typography>
              </Box>

              {/* Provider Questions - Rotating display */}
              {providerQuestions.length > 0 && (
                <Box sx={{ minHeight: 56, mb: 1.5, width: '100%' }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mb: 1, display: 'block', fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    Consider addressing:
                  </Typography>
                  <Fade in={fadeIn} timeout={600}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                        width: '100%',
                      }}
                    >
                      {getCurrentQuestions().map(question => (
                        <Box
                          key={question.id}
                          sx={{
                            bgcolor: 'background.paper',
                            px: 1,
                            py: 0.5,
                            borderRadius: 0.5,
                            border: '1px solid',
                            borderColor: 'info.light',
                            borderLeftWidth: 4,
                            borderLeftColor: 'info.main',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontSize: '0.7rem',
                              lineHeight: 1.4,
                              color: 'text.primary',
                            }}
                          >
                            {question.question}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Fade>
                </Box>
              )}

              {/* Text Input with Microphone */}
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  multiline
                  rows={3}
                  placeholder="Dictate your additions or edits..."
                  value={instructionText}
                  onChange={e => setInstructionText(e.target.value)}
                  disabled={isEditingNote || hasTooManySections}
                  sx={{
                    flex: '1 1 60%',
                    '& .MuiOutlinedInput-root': {
                      fontSize: '0.875rem',
                    },
                  }}
                  variant="outlined"
                  size="small"
                />
                <IconButton
                  onClick={handleRecordingToggle}
                  disabled={isEditingNote || hasTooManySections}
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: isRecording ? 'error.main' : 'primary.main',
                    color: 'white',
                    mt: 0.5,
                    flexShrink: 0,
                    '&:hover': {
                      backgroundColor: isRecording ? 'error.dark' : 'primary.dark',
                    },
                  }}
                >
                  {isRecording ? <MicOff sx={{ fontSize: 32 }} /> : <Mic sx={{ fontSize: 32 }} />}
                </IconButton>
              </Box>

              {/* Hidden Voice Component */}
              <VoiceInstructionInput
                onTranscript={handleVoiceTranscript}
                disabled={isEditingNote || hasTooManySections}
                hidden
                externalToggle={isRecording}
                onRecordingChange={setIsRecording}
              />

              {/* Submit Button */}
              <Button
                variant="contained"
                onClick={handleDictationSubmit}
                disabled={!instructionText.trim() || isEditingNote || hasTooManySections}
                startIcon={<Edit />}
                size="small"
                sx={{ alignSelf: 'flex-start', mt: 'auto' }}
              >
                Update Note
              </Button>

              {hasTooManySections && (
                <Typography variant="caption" color="warning.main" sx={{ fontSize: '0.7rem' }}>
                  Note has too many sections ({sectionCount}). Consider using a shorter template.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Feedback buttons centered */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Tooltip title="Mark as useful">
            <IconButton
              size="large"
              onClick={handleFeedbackUseful}
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'success.main',
                '&:hover': { bgcolor: 'success.light', color: 'success.dark' },
              }}
            >
              <CheckIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Mark as not useful">
            <IconButton
              size="large"
              onClick={handleFeedbackNotUseful}
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'error.main',
                '&:hover': { bgcolor: 'error.light', color: 'error.dark' },
              }}
            >
              <CloseIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Add feedback">
            <IconButton
              size="large"
              onClick={handleFeedbackComment}
              sx={{
                width: 56,
                height: 56,
                bgcolor: 'background.paper',
                border: '2px solid',
                borderColor: 'info.main',
                '&:hover': { bgcolor: 'info.light', color: 'info.dark' },
              }}
            >
              <CommentIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>

      {/* Edit Note Loading Modal */}
      {(isEditingNote || editError) && taskId && (
        <EditNoteLoadingModal
          open={isEditingNote || !!editError}
          taskId={taskId}
          status={status}
          ready={ready}
          error={editError}
          onClose={handleCloseModal}
        />
      )}
    </Card>
  );
};
